import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { FileText, Upload, Download, Loader2, Plus, AlertTriangle, X, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RH_CATEGORIAS = [
  { id: 'admissao', nome: 'Admissão' },
  { id: 'demissao', nome: 'Demissão' },
  { id: 'atestado', nome: 'Atestados' },
  { id: 'advertencia', nome: 'Advertências' },
  { id: 'outros', nome: 'Outros' },
];

const STATUS_CONFIG = {
  pendente: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente' },
  visualizado: { color: 'bg-blue-100 text-blue-800', label: 'Visualizado' },
  arquivado: { color: 'bg-gray-100 text-gray-800', label: 'Arquivado' },
  aguardando_assinatura: { color: 'bg-purple-100 text-purple-800', label: 'Aguardando Assinatura' },
  assinado: { color: 'bg-green-100 text-green-800', label: 'Assinado' },
};

const DocumentosFuncionarioSection = ({ funcionarioId, funcionarioNome }) => {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    tipo_documento_id: '',
    categoria_rh: '',
    descricao_outros: '',
    motivo: '',
    file: null
  });

  useEffect(() => {
    if (funcionarioId) {
      loadDocumentos();
      loadTiposDocumento();
    }
  }, [funcionarioId]);

  const loadTiposDocumento = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tipos-documentos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposDocumento(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };

  const loadDocumentos = async () => {
    if (!funcionarioId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/documentos/funcionario/${funcionarioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentos(response.data.documentos || []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    }
    setLoading(false);
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/documentos/download/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: doc.arquivo_tipo || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.arquivo_nome;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.tipo_documento_id || !uploadForm.categoria_rh) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (uploadForm.categoria_rh === 'outros' && !uploadForm.descricao_outros) {
      toast.error('Informe a descrição para "Outros"');
      return;
    }

    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('funcionario_id', funcionarioId);
      formData.append('tipo_documento_id', uploadForm.tipo_documento_id);
      formData.append('document_category', 'rh');
      formData.append('categoria_rh', uploadForm.categoria_rh);
      formData.append('descricao_outros', uploadForm.categoria_rh === 'outros' ? uploadForm.descricao_outros : '');
      formData.append('motivo', uploadForm.motivo);

      await axios.post(`${API}/api/documentos/upload/admin`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Documento enviado com sucesso');
      setShowUpload(false);
      setUploadForm({ tipo_documento_id: '', categoria_rh: '', descricao_outros: '', motivo: '', file: null });
      loadDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    }
    setEnviando(false);
  };

  const resetForm = () => {
    setUploadForm({ tipo_documento_id: '', categoria_rh: '', descricao_outros: '', motivo: '', file: null });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Documentos de {funcionarioNome}</span>
            <Button size="sm" onClick={() => setShowUpload(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Novo Documento
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : documentos.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Nenhum documento encontrado</p>
              <p className="text-sm text-gray-500 mt-1">Clique em "Novo Documento" para adicionar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Tipo</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Categoria</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Data</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {documentos.map(doc => (
                    <tr key={doc.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 text-sm">{doc.tipo_documento_nome}</td>
                      <td className="py-2 px-3">
                        <Badge variant="secondary" className="text-xs">
                          {RH_CATEGORIAS.find(c => c.id === doc.categoria_rh)?.nome || doc.categoria_rh}
                        </Badge>
                        {doc.categoria_rh === 'outros' && doc.descricao_outros && (
                          <p className="text-xs text-gray-500 mt-0.5">{doc.descricao_outros}</p>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={`text-xs ${(STATUS_CONFIG[doc.status] || STATUS_CONFIG.pendente).color}`}>
                          {(STATUS_CONFIG[doc.status] || STATUS_CONFIG.pendente).label}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-600">
                        {doc.criado_em ? new Date(doc.criado_em).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Upload */}
      <Dialog open={showUpload} onOpenChange={(open) => { setShowUpload(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Novo Documento - {funcionarioNome}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={uploadForm.categoria_rh} onValueChange={(v) => setUploadForm({...uploadForm, categoria_rh: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {RH_CATEGORIAS.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {uploadForm.categoria_rh === 'outros' && (
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input value={uploadForm.descricao_outros} onChange={(e) => setUploadForm({...uploadForm, descricao_outros: e.target.value})} placeholder="Descreva o tipo de documento" />
              </div>
            )}

            <div className="space-y-2">
              <Label>Tipo de Documento *</Label>
              <Select value={uploadForm.tipo_documento_id} onValueChange={(v) => setUploadForm({...uploadForm, tipo_documento_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDocumento.map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id}>{tipo.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Input value={uploadForm.motivo} onChange={(e) => setUploadForm({...uploadForm, motivo: e.target.value})} placeholder="Motivo do documento" />
            </div>

            <div className="space-y-2">
              <Label>Arquivo *</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowUpload(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" disabled={enviando}>
                {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Enviar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentosFuncionarioSection;
